/**
 * excelExport.js — Reusable Excel export utility (SheetJS/xlsx)
 *
 * Column definition schema:
 *   {
 *     header: string,          // Column header text shown in Excel
 *     key: string,             // Field name on the row object; supports "nested.field"
 *     width?: number,          // Column width in characters (default: 20)
 *     render?: (val, row) => * // Optional transform before writing to cell
 *   }
 *
 * Exported functions:
 *   exportToExcel(columns, rows, filename)
 *   exportMultiSheet(sheets, filename)
 *   exportMatrixSheet(headers, matrix, filename)
 */

import * as XLSX from "xlsx";
import i18n from "../i18n";
import { todayIso } from "./regionalFormat";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Resolve a potentially-nested key like "coach.name" from an object.
 */
function getNestedValue(obj, key) {
  if (!key || obj == null) return undefined;
  return key
    .split(".")
    .reduce((acc, k) => (acc != null ? acc[k] : undefined), obj);
}

/**
 * Auto-detect a sensible column width from the header and first few row values.
 */
function autoWidth(header, rows, key, renderFn) {
  let max = (header || "").length;
  const sample = rows.slice(0, 50);
  for (const row of sample) {
    const raw = getNestedValue(row, key);
    const val = renderFn ? renderFn(raw, row) : raw;
    const str = val == null ? "" : String(val);
    if (str.length > max) max = str.length;
  }
  return Math.min(Math.max(max + 2, 10), 60);
}

/**
 * Apply column widths to a worksheet.
 */
function applyColWidths(ws, colWidths) {
  ws["!cols"] = colWidths.map((w) => ({ wch: w }));
}

// ─── Core builders ────────────────────────────────────────────────────────────

/**
 * Safely call a render function, returning '' on any exception.
 */
function safeRender(renderFn, raw, row) {
  try {
    const val = renderFn(raw, row);
    return val == null ? "" : val;
  } catch {
    return raw == null ? "" : String(raw);
  }
}

/**
 * Build a worksheet from column definitions + row data.
 */
function buildSheet(columns, rows) {
  if (!columns || !columns.length) return XLSX.utils.aoa_to_sheet([]);

  const safeRows = Array.isArray(rows) ? rows.filter(Boolean) : [];

  // Header row
  const headerRow = columns.map((c) => c.header || c.key || "");

  // Data rows — every cell is guarded against null/undefined/render-throw
  const dataRows = safeRows.map((row) =>
    columns.map((col) => {
      try {
        const raw = getNestedValue(row, col.key);
        const val = col.render ? safeRender(col.render, raw, row) : raw;
        return val == null ? "" : val;
      } catch {
        return "";
      }
    }),
  );

  const ws = XLSX.utils.aoa_to_sheet([headerRow, ...dataRows]);

  // Column widths
  const widths = columns.map((col) => {
    if (col.width) return col.width;
    return autoWidth(col.header || col.key, safeRows, col.key, col.render);
  });
  applyColWidths(ws, widths);

  return ws;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Export a single flat table to Excel.
 *
 * @param {Array} columns   Column definitions
 * @param {Array} rows      Row data
 * @param {string} filename Base filename (date appended automatically)
 */
export function exportToExcel(columns, rows, filename) {
  try {
    const wb = XLSX.utils.book_new();
    const ws = buildSheet(columns, Array.isArray(rows) ? rows : []);
    XLSX.utils.book_append_sheet(wb, ws, i18n.t('exports.sheetReport', { defaultValue: 'Report' }));
    const dated = `${filename || "export"}-${todayIso()}`;
    XLSX.writeFile(wb, `${dated}.xlsx`);
  } catch (err) {
    console.error("[excelExport] exportToExcel failed:", err);
    alert(i18n.t('common.exportFailed'));
  }
}

/**
 * Export multiple sheets into one workbook.
 *
 * @param {Array} sheets  Array of { name, columns, rows }
 * @param {string} filename Base filename (date appended automatically)
 */
export function exportMultiSheet(sheets, filename) {
  try {
    const wb = XLSX.utils.book_new();
    for (const sheet of (sheets || [])) {
      const ws = buildSheet(sheet.columns, Array.isArray(sheet.rows) ? sheet.rows : []);
      const safeName = (sheet.name || i18n.t('exports.sheetGeneric', { defaultValue: 'Sheet' }))
        .slice(0, 31)
        .replace(/[\\/:*?[\]]/g, "_");
      XLSX.utils.book_append_sheet(wb, ws, safeName);
    }
    const dated = `${filename || "export"}-${todayIso()}`;
    XLSX.writeFile(wb, `${dated}.xlsx`);
  } catch (err) {
    console.error("[excelExport] exportMultiSheet failed:", err);
    alert(i18n.t('common.exportFailed'));
  }
}

/**
 * Export a matrix/pivot table (dynamic columns) to Excel.
 * The matrix is already a 2D array where matrix[0] is the header row.
 *
 * @param {string[][]} matrix  2D array including headers as first row
 * @param {string} filename    Base filename (date appended automatically)
 */
export function exportMatrixSheet(matrix, filename) {
  try {
    const safeMatrix = Array.isArray(matrix) ? matrix.filter(Array.isArray) : [];
    if (!safeMatrix.length) {
      alert(i18n.t('common.noDataExport'));
      return;
    }
    const wb = XLSX.utils.book_new();
    // Sanitize every cell: null/undefined → ""
    const cleaned = safeMatrix.map((row) =>
      row.map((cell) => (cell == null ? "" : cell))
    );
    const ws = XLSX.utils.aoa_to_sheet(cleaned);

    // Auto-size columns based on content
    const numCols = (safeMatrix[0] || []).length;
    const colWidths = [];
    for (let c = 0; c < numCols; c++) {
      let max = 10;
      for (let r = 0; r < Math.min(safeMatrix.length, 100); r++) {
        const cell = safeMatrix[r][c];
        const len = cell == null ? 0 : String(cell).length;
        if (len > max) max = len;
      }
      colWidths.push({ wch: Math.min(max + 2, 60) });
    }
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, i18n.t('exports.sheetReport', { defaultValue: 'Report' }));
    const dated = `${filename || "export"}-${todayIso()}`;
    XLSX.writeFile(wb, `${dated}.xlsx`);
  } catch (err) {
    console.error("[excelExport] exportMatrixSheet failed:", err);
    alert(i18n.t('common.exportFailed'));
  }
}
